import React from "react";
import CustomLink from "../../atoms/CustomLink/CustomLink.atom";
import { NavItem } from "../../../interfaces/Navigation.interface";
import DropdownMenu from "../DropdownMenu/DropdownMenu.molecule";
import ChevronDown from "../../../assets/svgs/ChevronDown.svg";
import { StoryblokLink } from "../../../interfaces/Storyblok/StoryblokLink.interface";
import Button from "../../atoms/Button/Button.component";
import { ButtonTypes } from "../../../interfaces/enums/ButtonTypes.enum";
import { ButtonSizeTypes } from "../../../interfaces/enums/ButtonSizeTypes.enum";
import SocialLink from "../../atoms/SocialLink/SocialLink.atom";
import { StoryblokNode } from "../../../interfaces/Storyblok/StoryblokNode.interface";
import VenueIndexSearchBar from "../VenueIndexSearchBar/VenueIndexSearchBar.molecule";
import classNames from "classnames";

interface PreNavProps {
  links: NavItem[];
  login_link: StoryblokLink;
  social_links: StoryblokNode[];
  pre_selected_filter_options: string[];
  is_find_us_search_enabled: boolean;
  find_us_link: StoryblokLink;
  find_us_placeholder: string;
  find_us_button_label: string;
}

const PreNav: React.FC<PreNavProps> = ({
  links,
  login_link,
  social_links,
  pre_selected_filter_options,
  is_find_us_search_enabled,
  find_us_link,
  find_us_placeholder,
  find_us_button_label,
}) => {
  const handleDataLayerEvent = (label: string, link: string): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "click",
      type: "Pre Nav Menu",
      button: {
        text: label,
        url: link,
      },
    });
  };

  return (
    <nav
      className={classNames(`lg:flex items-center`, {
        "justify-end": !is_find_us_search_enabled,
        "justify-between": is_find_us_search_enabled,
      })}
      aria-label="Pre-navigation"
    >
      {is_find_us_search_enabled && (
        <VenueIndexSearchBar
          preSelectedFilters={pre_selected_filter_options}
          placeholder_text={find_us_placeholder}
          button={[{ link: find_us_link, label: find_us_button_label }]}
          type={"small"}
          btnId={"findClubsBtnPreNav"}
        ></VenueIndexSearchBar>
      )}
      <div className="lg:flex items-center justify-end space-x-3 xl:space-x-6">
        <ul className="flex text-sherpa-pre-nav-text text-White lg:space-x-3 xl:space-x-6">
          {links &&
            !!links.length &&
            links
              .filter((_, idx) => (is_find_us_search_enabled ? idx < 2 : true))
              .map(
                (link, idx) =>
                  link.display_name && (
                    <li key={link._uid} className="relative flex items-center group mx-0 last:mr-0">
                      <CustomLink
                        url={link.page_link.cached_url}
                        className="h-[72px] flex justify-center items-center text-sm xl:text-base group"
                        onClick={() => handleDataLayerEvent(link.display_name, link.page_link.cached_url)}
                      >
                        <span className="pr-2 group-hover:text-SP1 group-hover:!text-sherpa-yellow-100 text-sm 3xl:text-base">
                          {link.display_name}
                        </span>
                        {!!link.dropdown_list?.length && (
                          <ChevronDown
                            className="text-sherpa-pre-nav-text text-White group-hover:text-SP1 group-hover:!text-sherpa-yellow-100"
                            rotateOnHover={true}
                          />
                        )}
                      </CustomLink>
                      {!!link.dropdown_list?.length && (
                        <DropdownMenu
                          links={link.dropdown_list}
                          position="rounded-b-2xl drop-shadow-2xl lg:-left-14 xl:left-0 top-[99%]"
                          dataLayerEventType="Pre Nav Item Menu Dropdown"
                          parentLinkLabel={link.display_name}
                        />
                      )}
                    </li>
                  ),
              )}
        </ul>
        <div className="flex items-center space-x-4">
          {social_links && !!social_links.length && (
            <div className="flex justify-center items-center flex-wrap md:w-full">
              {social_links.map((social_link, i) => (
                <SocialLink
                  key={social_link.content._uid}
                  type={social_link.content.type}
                  url={social_link.content.url}
                  size="sm"
                />
              ))}
            </div>
          )}
          {login_link?.cached_url && (
            <Button
              link={login_link.cached_url}
              label={"LOGIN"}
              type={ButtonTypes.PRIMARY}
              size={ButtonSizeTypes.DEFAULT}
              className="bg-Secondary-100 hover:bg-Secondary-200 !bg-sherpa-white !text-sherpa-blue-900 hover:!bg-sherpa-blue-150"
            />
          )}
        </div>
      </div>
    </nav>
  );
};

export default PreNav;
